/** How long a sync catch-up after a broker move may run before it is reported `timed-out`; it keeps running. */
export const SYNC_CATCH_UP_TIMEOUT_MS = 60_000;

/** A sync catch-up's `SyncCatchUp` report. */
export interface SyncCatchUpReport {
    outcome: 'started' | 'caught-up' | 'failed' | 'timed-out' | 'skipped';
    partitions?: number;
    failed?: number;
}

/**
 * Catches sync up after a broker move and always reports it: `skipped` when the client never reached the new broker,
 * else `started`, then `caught-up` or `failed`, or `timed-out` after {@link SYNC_CATCH_UP_TIMEOUT_MS}. */
export async function catchUpSyncAndReport(
    catchUp: (movedAt: number) => Promise<{ partitions: number, failed: number }>,
    report: (payload: SyncCatchUpReport) => void,
    movedAt: number,
    reached: boolean,
): Promise<void> {
    if (!reached) {
        report({ outcome: 'skipped' });
        return;
    }
    report({ outcome: 'started' });
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        const result = await Promise.race([
            catchUp(movedAt),
            new Promise<undefined>(resolve => { timer = setTimeout(() => resolve(undefined), SYNC_CATCH_UP_TIMEOUT_MS); }),
        ]);
        if (!result) report({ outcome: 'timed-out' });
        else report({ outcome: result.failed ? 'failed' : 'caught-up', partitions: result.partitions, failed: result.failed });
    } finally {
        clearTimeout(timer);
    }
}
